import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { encodeQr } from './qr-encoder';
import { POSTER_LOGOS, PosterLogo } from './poster-logos';

export type PosterLayoutId = 'corner' | 'column' | 'centre';
export type PosterInk = 'dark' | 'light';

export interface PosterCanvas {
  w: number;
  h: number;
  label: string;
  layouts: PosterLayoutId[];
}

/** Poster artwork palette — the HUNGERY print/brand look, independent of the admin UI theme. */
interface PosterTheme {
  ink: string;
  mute: string;
  rule: string;
  scrim: string;
  a1: number;
  a2: number;
  panel: string;
  edge: string;
  qrInk: string;
  shadow: string;
}

interface TextStyle {
  ff: string;
  fs: number;
  fw?: number;
  ls?: number;
}

interface TextSpec extends TextStyle {
  x: number;
  y: number;
  fill: string;
  anchor?: 'start' | 'middle' | 'end';
}

/** Everything a layout needs to draw itself. */
interface DrawContext {
  g: SVGGElement;
  T: PosterTheme;
  s: number;
  pad: number;
  W: number;
  H: number;
  measure: (str: string, o: TextStyle) => number;
}

const NS = 'http://www.w3.org/2000/svg';
const DISPLAY = '"Bodoni Moda", Georgia, "Times New Roman", serif';
const UTIL = '"Hanken Grotesk", system-ui, -apple-system, sans-serif';
const CTA = 'Scan to claim';
const FALLBACK_QR_URL = 'https://menu.hungery.id';

/**
 * Which HUNGERY lockup belongs in which slot: the horizontal logo along the top and bottom
 * edges, the vertical one inside the composition, and the bare mark where the space is
 * small and the job is just to sign the code.
 */
type LogoLockup = 'horizontal' | 'vertical' | 'icon';

const THEMES: Record<PosterInk, PosterTheme> = {
  dark: {
    ink: '#F4F2ED', mute: 'rgba(244,242,237,.62)', rule: 'rgba(244,242,237,.36)',
    scrim: '#0B0B09', a1: 0.42, a2: 0.8, panel: '#F4F2ED', edge: 'rgba(11,11,9,.10)',
    qrInk: '#141410', shadow: '0 1px 3px rgba(0,0,0,.55)'
  },
  light: {
    ink: '#141410', mute: 'rgba(20,20,16,.66)', rule: 'rgba(20,20,16,.32)',
    scrim: '#F8F6F1', a1: 0.52, a2: 0.88, panel: '#FFFFFF', edge: 'rgba(20,20,16,.22)',
    qrInk: '#141410', shadow: '0 1px 3px rgba(255,255,255,.6)'
  }
};

const CANVASES: PosterCanvas[] = [
  { w: 1080, h: 1350, label: '4:5', layouts: ['corner', 'column', 'centre'] },
  { w: 1080, h: 1080, label: '1:1', layouts: ['corner', 'column', 'centre'] },
  { w: 1080, h: 1920, label: '9:16', layouts: ['corner', 'column', 'centre'] },
  { w: 1920, h: 1080, label: '16:9', layouts: ['corner', 'centre'] }
];

const LAYOUT_META: Record<PosterLayoutId, { title: string; hint: string }> = {
  corner: { title: 'Corner', hint: 'Framed. Words bottom-left, code cut into the bottom-right corner.' },
  column: { title: 'Column', hint: 'Single left column. The whole right side of the photo stays clear.' },
  centre: { title: 'Centre', hint: 'Symmetrical. Everything stacked and centred along the lower axis.' }
};

const POSTER_FONT_LINK_ID = 'mini-menu-qr-poster-fonts';
// The poster artwork is set in Bodoni Moda over Hanken Grotesk. Same reasoning as
// ensureMiniMenuThemeFonts: Angular strips `@import url(...)` from component stylesheets,
// so without this link the preview and its text measurements fall back to system fonts.
const POSTER_FONT_HREF = 'https://fonts.googleapis.com/css2'
  + '?family=Bodoni+Moda:opsz,wght@6..96,400;6..96,500'
  + '&family=Hanken+Grotesk:wght@400;500;600'
  + '&display=swap';

function ensurePosterFonts(doc: Document, onLoad: () => void): void {
  if (doc.getElementById(POSTER_FONT_LINK_ID)) {
    return;
  }
  const link = doc.createElement('link');
  link.id = POSTER_FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href = POSTER_FONT_HREF;
  link.addEventListener('load', onLoad);
  doc.head.appendChild(link);
}

/**
 * QR poster designer. A live poster composite (the operator's photo under a transparent
 * SVG overlay) beside a rail of decisions — size, layout, photo, ink, business, words —
 * ending in an SVG download. The QR encodes the business's public MiniMenu link.
 *
 * The overlay drawing code is the approved prototype from
 * UI Concepts/hungery-overlay-simple.html, ported method-for-method; only the chrome
 * around it was rebuilt to the admin design language.
 */
@Component({
  selector: 'app-mini-menu-qr-poster-modal',
  templateUrl: './mini-menu-qr-poster-modal.component.html',
  styleUrls: ['./mini-menu-qr-poster-modal.component.scss']
})
export class MiniMenuQrPosterModalComponent implements OnInit, AfterViewInit, OnDestroy {
  // Set by the opener before the modal renders.
  businessName = 'Your Business';
  businessDetail = '';
  menuUrl = '';

  @ViewChild('composite') compositeRef!: ElementRef<HTMLDivElement>;
  @ViewChild('artHost') artRef!: ElementRef<HTMLDivElement>;

  readonly canvases = CANVASES;
  readonly layoutMeta = LAYOUT_META;

  W = 1080;
  H = 1350;
  layout: PosterLayoutId = 'corner';
  ink: PosterInk = 'dark';
  bizName = '';
  bizDetail = '';
  logoImg: string | null = null;
  // Sample offer so the poster reads finished from the first frame; the operator overtypes it.
  eyebrow = 'This week only';
  headline = 'Two for one\non every pour-over';
  ctaUrl = '';
  qrNote = '—';

  image: { src: string; w: number; h: number } | null = null;
  imgMsgKind: 'none' | 'ok' | 'warn' = 'none';
  imgMsgDims = '';
  imgMsgText = '';

  private viewReady = false;
  private destroyed = false;

  constructor(private activeModal: NgbActiveModal, @Inject(DOCUMENT) private document: Document) {}

  ngOnInit(): void {
    this.bizName = this.businessName;
    this.bizDetail = this.businessDetail;
    this.ctaUrl = this.menuUrl || FALLBACK_QR_URL;
    this.checkImage();
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    ensurePosterFonts(this.document, () => this.render());
    this.compositeRef.nativeElement.style.setProperty('--ar', String(this.H / this.W));
    // A tick later, not inline: drawing sets qrNote, which is template-bound, and mutating it
    // inside the first change-detection pass is an NG0100 in dev mode.
    setTimeout(() => this.render());
    // Re-measure once the webfonts are in; until then the headline wraps against fallback metrics.
    const fonts = this.document.fonts;
    if (fonts && fonts.ready) {
      fonts.ready.then(() => this.render());
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.image) {
      URL.revokeObjectURL(this.image.src);
    }
  }

  close(): void {
    this.activeModal.dismiss('cancel');
  }

  get availableLayouts(): PosterLayoutId[] {
    const canvas = CANVASES.find((c) => c.w === this.W && c.h === this.H);
    return canvas ? canvas.layouts : CANVASES[0].layouts;
  }

  setCanvas(c: PosterCanvas): void {
    this.W = c.w;
    this.H = c.h;
    if (!c.layouts.includes(this.layout)) {
      this.layout = c.layouts[0];
    }
    this.compositeRef.nativeElement.style.setProperty('--ar', String(this.H / this.W));
    this.checkImage();
    this.render();
  }

  setLayout(id: PosterLayoutId): void {
    this.layout = id;
    this.render();
  }

  setInk(ink: PosterInk): void {
    this.ink = ink;
    this.render();
  }

  onPhotoSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) {
      return;
    }
    const src = URL.createObjectURL(file);
    const probe = new Image();
    probe.onload = () => {
      if (this.image) {
        URL.revokeObjectURL(this.image.src);
      }
      this.image = { src, w: probe.naturalWidth, h: probe.naturalHeight };
      this.checkImage();
    };
    probe.onerror = () => {
      URL.revokeObjectURL(src);
      this.imgMsgKind = 'warn';
      this.imgMsgDims = '';
      this.imgMsgText = 'That file could not be read as an image.';
    };
    probe.src = src;
  }

  clearPhoto(): void {
    if (this.image) {
      URL.revokeObjectURL(this.image.src);
    }
    this.image = null;
    this.checkImage();
  }

  onLogoSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.logoImg = String(reader.result);
      this.render();
    };
    reader.readAsDataURL(file);
  }

  clearLogo(): void {
    this.logoImg = null;
    this.render();
  }

  downloadSvg(): void {
    const svg = this.artRef.nativeElement.querySelector('svg');
    if (!svg) {
      return;
    }
    const copy = svg.cloneNode(true) as SVGSVGElement;
    copy.setAttribute('width', String(this.W));
    copy.setAttribute('height', String(this.H));
    copy.removeAttribute('style');
    const blob = new Blob(
      ['<?xml version="1.0" encoding="UTF-8"?>\n' + new XMLSerializer().serializeToString(copy)],
      { type: 'image/svg+xml' }
    );
    const a = this.document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `hungery-qr-poster-${this.layout}-${this.W}x${this.H}.svg`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  }

  /** Aspect/resolution advice for the supplied photo, in plain words. */
  private checkImage(): void {
    if (!this.image) {
      this.imgMsgKind = 'none';
      this.imgMsgDims = '';
      this.imgMsgText = 'No photo yet — the checkerboard shows the overlay carries no background of its own.';
      return;
    }
    const target = this.W / this.H;
    const actual = this.image.w / this.image.h;
    const off = Math.abs(actual - target) / target;
    const lowRes = this.image.w < this.W || this.image.h < this.H;
    const resNote = lowRes ? ` Also below the recommended ${this.W} × ${this.H} — it will soften when scaled up.` : '';
    this.imgMsgDims = `${this.image.w} × ${this.image.h}`;

    if (off < 0.01) {
      this.imgMsgKind = 'ok';
      this.imgMsgText = `— matches the canvas. Fills the frame edge to edge.${resNote}`;
    } else {
      const bars = actual > target ? 'white bars above and below it' : 'white bars down either side';
      const crop = actual > target ? 'taller' : 'wider';
      this.imgMsgKind = 'warn';
      this.imgMsgText = `does not match this canvas. It will sit inside the frame with ${bars}, `
        + `and the overlay's shading will fall across them. Re-crop ${crop} to `
        + `${this.W} × ${this.H} to fill the frame.${resNote}`;
    }
  }

  /* ------------------------------------------------------------------
     Overlay drawing — ported from the prototype. All geometry unchanged.
     ------------------------------------------------------------------ */

  private el<K extends keyof SVGElementTagNameMap>(name: K, attrs: Record<string, string | number> = {}): SVGElementTagNameMap[K] {
    const node = this.document.createElementNS(NS, name);
    for (const key of Object.keys(attrs)) {
      node.setAttribute(key, String(attrs[key]));
    }
    return node;
  }

  private txt(g: SVGGElement, str: string, o: TextSpec): SVGTextElement {
    let x = o.x;
    if (o.ls && o.anchor === 'middle') {
      x += o.ls / 2;
    }
    if (o.ls && o.anchor === 'end') {
      x += o.ls;
    }
    const t = this.el('text', {
      x, y: o.y, fill: o.fill, 'font-family': o.ff, 'font-size': o.fs,
      'font-weight': o.fw || 400, 'text-anchor': o.anchor || 'start'
    });
    if (o.ls) {
      t.setAttribute('letter-spacing', String(o.ls));
    }
    t.textContent = str;
    g.appendChild(t);
    return t;
  }

  private line(g: SVGGElement, x1: number, y1: number, x2: number, y2: number, T: PosterTheme, s: number): void {
    g.appendChild(this.el('line', {
      x1, y1, x2, y2, stroke: T.rule, 'stroke-width': Math.max(1, 1.25 * s), 'stroke-linecap': 'square'
    }));
  }

  /**
   * The HUNGERY mark for the current ink. Brand purple is the default; over a dark photo the
   * light ink carries the same artwork reversed, because purple on near-black is unreadable.
   */
  private logo(lockup: LogoLockup): PosterLogo {
    const tone = this.ink === 'dark' ? 'Light' : 'Purple';
    return POSTER_LOGOS[lockup + tone];
  }

  /** Width ÷ height of a lockup — needed before drawing, to part the hairlines around it. */
  private logoRatio(lockup: LogoLockup): number {
    const art = this.logo(lockup);
    return art.w / art.h;
  }

  /**
   * Places a mark by its height — width follows the artwork's own ratio, so no lockup is ever
   * stretched. `align` is horizontal about `x`; the mark hangs from `top`.
   */
  private drawLogo(
    g: SVGGElement,
    lockup: LogoLockup,
    o: { x: number; top: number; height: number; align?: 'start' | 'middle' | 'end' }
  ): { w: number; h: number } {
    const art = this.logo(lockup);
    const h = o.height;
    const w = h * (art.w / art.h);
    const x = o.align === 'middle' ? o.x - w / 2 : o.align === 'end' ? o.x - w : o.x;
    g.appendChild(this.el('image', {
      href: art.href, x, y: o.top, width: w, height: h, preserveAspectRatio: 'xMidYMid meet'
    }));
    return { w, h };
  }

  /** Words: eyebrow + headline. Returns the cap-top of the highest line. */
  private drawWords(c: DrawContext, o: { x: number; bottom: number; align: 'start' | 'middle' | 'end'; maxW: number; fs: number }): number {
    const { g, T, s, measure } = c;
    const rawLines = this.headline.split('\n').map((l) => l.trim()).filter(Boolean);
    let headFs = o.fs;
    let lines: string[] = [];
    for (;;) {
      lines = [];
      for (const raw of rawLines) {
        let cur = '';
        for (const w of raw.split(/\s+/)) {
          const test = cur ? cur + ' ' + w : w;
          if (cur && measure(test, { ff: DISPLAY, fs: headFs, fw: 500 }) > o.maxW) {
            lines.push(cur);
            cur = w;
          } else {
            cur = test;
          }
        }
        if (cur) {
          lines.push(cur);
        }
      }
      if (lines.length <= 3 || headFs <= 44 * s) {
        break;
      }
      headFs -= 5 * s;
    }
    const lead = headFs * 0.93;
    lines.forEach((l, i) => this.txt(g, l, {
      x: o.x, y: o.bottom - (lines.length - 1 - i) * lead, fill: T.ink,
      ff: DISPLAY, fs: headFs, fw: 500, anchor: o.align
    }));
    let top = o.bottom - (lines.length - 1) * lead - headFs * 0.7;
    if (this.eyebrow.trim()) {
      const efs = 19 * s;
      this.txt(g, this.eyebrow.toUpperCase(), {
        x: o.x, y: top - 42 * s, fill: T.mute, ff: UTIL, fs: efs, fw: 600, ls: efs * 0.34, anchor: o.align
      });
      top = top - 42 * s - efs * 0.72;
    }
    return top;
  }

  /** Business: mark + name + detail. `bottom` is the detail baseline. Returns cap-top. */
  private drawBusiness(c: DrawContext, o: { x: number; bottom: number; align: 'start' | 'middle' }): number {
    const { g, T, s } = c;
    const nameFs = 38 * s;
    const detFs = 19 * s;
    const markSize = 56 * s;
    const nameBase = o.bottom - 38 * s;
    const nameTop = nameBase - nameFs * 0.7;
    const initials = this.bizName.trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase();

    const mark = (mx: number, my: number): void => {
      if (this.logoImg) {
        g.appendChild(this.el('image', {
          href: this.logoImg, x: mx, y: my, width: markSize, height: markSize, preserveAspectRatio: 'xMidYMid meet'
        }));
      } else {
        g.appendChild(this.el('circle', {
          cx: mx + markSize / 2, cy: my + markSize / 2, r: markSize / 2,
          fill: 'none', stroke: T.rule, 'stroke-width': Math.max(1, 1.25 * s)
        }));
        this.txt(g, initials, {
          x: mx + markSize / 2, y: my + markSize / 2 + 22 * s * 0.36,
          fill: T.ink, ff: DISPLAY, fs: 22 * s, fw: 500, anchor: 'middle'
        });
      }
    };

    if (o.align === 'middle') {
      const markTop = nameTop - 26 * s - markSize;
      mark(o.x - markSize / 2, markTop);
      this.txt(g, this.bizName, { x: o.x, y: nameBase, fill: T.ink, ff: DISPLAY, fs: nameFs, fw: 500, anchor: 'middle' });
      this.txt(g, this.bizDetail.toUpperCase(), {
        x: o.x, y: o.bottom, fill: T.mute, ff: UTIL, fs: detFs, fw: 500, ls: detFs * 0.16, anchor: 'middle'
      });
      return markTop;
    }
    const rowMid = (nameTop + o.bottom) / 2;
    mark(o.x, rowMid - markSize / 2);
    const tx = o.x + markSize + 22 * s;
    this.txt(g, this.bizName, { x: tx, y: nameBase, fill: T.ink, ff: DISPLAY, fs: nameFs, fw: 500 });
    this.txt(g, this.bizDetail.toUpperCase(), {
      x: tx, y: o.bottom, fill: T.mute, ff: UTIL, fs: detFs, fw: 500, ls: detFs * 0.16
    });
    return Math.min(nameTop, rowMid - markSize / 2);
  }

  /** QR panel. x,y is the top-left of the panel. */
  private drawQR(c: DrawContext, o: { x: number; y: number; size: number }): void {
    const { g, T } = c;
    const q = encodeQr(this.ctaUrl || FALLBACK_QR_URL);
    this.qrNote = `Live code · version ${q.version} · ${q.size}×${q.size} modules · EC level M`;
    const quiet = o.size * 0.085;
    g.appendChild(this.el('rect', {
      x: o.x, y: o.y, width: o.size, height: o.size, fill: T.panel, stroke: T.edge,
      'stroke-width': Math.max(1, 1.25 * c.s)
    }));
    const cell = (o.size - quiet * 2) / q.size;
    let d = '';
    for (let r = 0; r < q.size; r++) {
      for (let col = 0; col < q.size; col++) {
        if (q.modules[r][col]) {
          const px = o.x + quiet + col * cell;
          const py = o.y + quiet + r * cell;
          d += `M${px.toFixed(2)} ${py.toFixed(2)}h${cell.toFixed(2)}v${cell.toFixed(2)}h${(-cell).toFixed(2)}z`;
        }
      }
    }
    g.appendChild(this.el('path', { d, fill: T.qrInk, 'shape-rendering': 'crispEdges' }));
  }

  private shortUrl(): string {
    return this.ctaUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
  }

  private drawCorner(c: DrawContext): void {
    const { g, T, s, pad, W, H, measure } = c;
    const panel = 208 * s;
    const qrR = W - pad;
    const qrB = H - pad;
    const qrL = qrR - panel;
    const qrT = qrB - panel;
    const cFs = 19 * s;
    const urlBase = qrT - 30 * s;
    const labBase = urlBase - 32 * s;
    const capTop = labBase - cFs * 0.72;
    const gap = 26 * s;

    // Top edge: the horizontal lockup, sitting astride the rule with the hairlines parted
    // around it — the same silhouette the wordmark used to cut.
    const markH = 62 * s;
    const markW = markH * this.logoRatio('horizontal');
    this.line(g, pad, pad, W / 2 - markW / 2 - gap, pad, T, s);
    this.line(g, W / 2 + markW / 2 + gap, pad, W - pad, pad, T, s);
    this.line(g, pad, pad, pad, H - pad, T, s);
    this.line(g, W - pad, pad, W - pad, capTop - 24 * s, T, s);
    this.line(g, pad, H - pad, qrL - gap, H - pad, T, s);
    this.drawLogo(g, 'horizontal', { x: W / 2, top: pad - markH / 2, height: markH, align: 'middle' });

    const bizTop = this.drawBusiness(c, { x: pad, bottom: H - pad - 44 * s, align: 'start' });
    const ruleY = bizTop - 46 * s;
    this.line(g, pad, ruleY, pad + 96 * s, ruleY, T, s);
    this.drawWords(c, {
      x: pad, bottom: ruleY - 46 * s, align: 'start',
      maxW: Math.min(qrL - gap * 2 - pad, (W - pad * 2) * 0.68, 720 * s), fs: 86 * s
    });

    this.txt(g, CTA.toUpperCase(), { x: qrR, y: labBase, fill: T.ink, ff: UTIL, fs: cFs, fw: 600, ls: cFs * 0.2, anchor: 'end' });
    this.txt(g, this.shortUrl(), { x: qrR, y: urlBase, fill: T.mute, ff: UTIL, fs: cFs, fw: 400, anchor: 'end' });

    // The bare mark signs the code: set just left of "SCAN TO CLAIM", centred on that line.
    const labW = measure(CTA.toUpperCase(), { ff: UTIL, fs: cFs, fw: 600, ls: cFs * 0.2 });
    const iconH = 42 * s;
    this.drawLogo(g, 'icon', {
      x: qrR - labW - 16 * s, top: labBase - cFs * 0.72 / 2 - iconH / 2, height: iconH, align: 'end'
    });

    this.drawQR(c, { x: qrL, y: qrT, size: panel });
  }

  private drawColumn(c: DrawContext): void {
    const { g, T, s, pad, W, H } = c;

    // Top edge, left-aligned: the horizontal lockup, rule running off its right shoulder.
    const markH = 58 * s;
    const mark = this.drawLogo(g, 'horizontal', { x: pad, top: pad - markH / 2, height: markH });
    this.line(g, pad + mark.w + 26 * s, pad, W - pad, pad, T, s);

    const panel = 176 * s;
    const qrT = H - pad - panel;
    this.drawQR(c, { x: pad, y: qrT, size: panel });
    const cFs = 19 * s;
    const tx = pad + panel + 26 * s;
    this.txt(g, CTA.toUpperCase(), { x: tx, y: H - pad - 40 * s, fill: T.ink, ff: UTIL, fs: cFs, fw: 600, ls: cFs * 0.2 });
    this.txt(g, this.shortUrl(), { x: tx, y: H - pad - 8 * s, fill: T.mute, ff: UTIL, fs: cFs, fw: 400 });

    // The mark closes the scan row at the right margin, balancing the code across the column.
    const iconH = 46 * s;
    this.drawLogo(g, 'icon', { x: W - pad, top: qrT + panel / 2 - iconH / 2, height: iconH, align: 'end' });

    const bizTop = this.drawBusiness(c, { x: pad, bottom: qrT - 46 * s, align: 'start' });
    const ruleY = bizTop - 44 * s;
    this.line(g, pad, ruleY, pad + 96 * s, ruleY, T, s);
    this.drawWords(c, { x: pad, bottom: ruleY - 46 * s, align: 'start', maxW: Math.min(W - pad * 2, 700 * s), fs: 80 * s });
  }

  private drawCentre(c: DrawContext): void {
    const { g, T, s, pad, W, H } = c;
    const gap = 26 * s;

    // Top edge: horizontal lockup between parted hairlines, as in the corner layout — held
    // smaller here so the vertical lockup below stays the primary brand statement.
    const markH = 52 * s;
    const markW = markH * this.logoRatio('horizontal');
    this.line(g, pad, pad, W / 2 - markW / 2 - gap, pad, T, s);
    this.line(g, W / 2 + markW / 2 + gap, pad, W - pad, pad, T, s);
    this.drawLogo(g, 'horizontal', { x: W / 2, top: pad - markH / 2, height: markH, align: 'middle' });

    const cx = W / 2;
    const cFs = 18 * s;
    const labBase = H - pad;

    // This is the one stacked, symmetrical slot in the set, so the code is signed with the
    // vertical lockup rather than the bare mark: logo under the code, instruction under both.
    // Sized so the wordmark under the mark is still legible in print, not a smudge.
    const stackH = 116 * s;
    const stackTop = labBase - cFs - 26 * s - stackH;
    const panel = 156 * s;
    const panelB = stackTop - 24 * s;
    const panelT = panelB - panel;

    this.txt(g, (CTA + ' · ' + this.shortUrl()).toUpperCase(), {
      x: cx, y: labBase, fill: T.mute, ff: UTIL, fs: cFs, fw: 500, ls: cFs * 0.16, anchor: 'middle'
    });
    this.drawLogo(g, 'vertical', { x: cx, top: stackTop, height: stackH, align: 'middle' });
    this.drawQR(c, { x: cx - panel / 2, y: panelT, size: panel });

    const bizTop = this.drawBusiness(c, { x: cx, bottom: panelT - 46 * s, align: 'middle' });
    const ruleY = bizTop - 42 * s;
    this.line(g, cx - 36 * s, ruleY, cx + 36 * s, ruleY, T, s);
    this.drawWords(c, { x: cx, bottom: ruleY - 46 * s, align: 'middle', maxW: Math.min((W - pad * 2) * 0.78, 760 * s), fs: 72 * s });
  }

  render(): void {
    if (!this.viewReady || this.destroyed) {
      return;
    }
    const T = THEMES[this.ink];
    const { W, H } = this;
    const s = Math.min(W, H) / 1080;
    const pad = 72 * s;
    const svg = this.el('svg', { viewBox: `0 0 ${W} ${H}`, xmlns: NS, preserveAspectRatio: 'xMidYMid slice' });
    // Inline because the svg is created outside the template: Angular's emulated view
    // encapsulation would never match it from the component stylesheet.
    svg.setAttribute('style', 'width:100%;height:100%;display:block');

    const rig = this.el('text', { visibility: 'hidden' });
    svg.appendChild(rig);
    this.artRef.nativeElement.replaceChildren(svg);
    const measure = (str: string, o: TextStyle): number => {
      rig.setAttribute('font-family', o.ff);
      rig.setAttribute('font-size', String(o.fs));
      rig.setAttribute('font-weight', String(o.fw || 400));
      rig.setAttribute('letter-spacing', String(o.ls || 0));
      rig.textContent = str;
      const w = rig.getComputedTextLength();
      return o.ls ? w - o.ls : w;
    };

    const defs = this.el('defs');
    const lg = this.el('linearGradient', { id: 'scrim', x1: '0', y1: '0', x2: '0', y2: '1' });
    const stops: Array<[string, number]> = [['44%', 0], ['74%', T.a1], ['100%', T.a2]];
    for (const [off, op] of stops) {
      lg.appendChild(this.el('stop', { offset: off, 'stop-color': T.scrim, 'stop-opacity': op }));
    }
    defs.appendChild(lg);
    svg.appendChild(defs);
    svg.appendChild(this.el('rect', { x: 0, y: 0, width: W, height: H, fill: 'url(#scrim)' }));

    const g = this.el('g', { style: `filter:drop-shadow(${T.shadow})` });
    svg.appendChild(g);

    const c: DrawContext = { g, T, s, pad, W, H, measure };
    try {
      switch (this.layout) {
        case 'corner':
          this.drawCorner(c);
          break;
        case 'column':
          this.drawColumn(c);
          break;
        case 'centre':
          this.drawCentre(c);
          break;
      }
    } catch (err) {
      this.qrNote = err instanceof Error ? err.message : String(err);
    }
    rig.remove();
  }
}
